"use client";

import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/compat/router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  hasActivePath,
  isMenuNavigationItem,
  normalizePath,
} from "@/lib/storefront-shell.mjs";
import { StorefrontIcon } from "@/components/storefront-icon";
import { fraunces as menuTileLabelFont } from "@/fonts";
import type { MergedMenuCategory } from "@/lib/storefront-shell-types";
import type { NavigationItem } from "@/lib/storefront-types";

type NavItemProps = {
  item: NavigationItem;
  menuCategories?: MergedMenuCategory[];
};

const MENU_CLOSE_DELAY_MS = 140;

function ChevronDownIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 10 10"
      className="h-2.5 w-2.5"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M2 3.5 5 6.5 8 3.5"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function DropdownChildren({
  items,
  onNavigate,
}: {
  items: NavigationItem[];
  onNavigate: () => void;
}) {
  return (
    <ul className="space-y-1">
      {items.map((item) => (
        <li key={item.key}>
          <Link
            href={item.path}
            onClick={onNavigate}
            className="block border border-transparent px-3 py-2.5 text-[0.84rem] font-medium text-white/82 transition-colors duration-150 hover:border-white/12 hover:bg-white/7 hover:text-white"
          >
            {item.label}
          </Link>
          {item.children.length > 0 ? (
            <div className="ml-3 mt-2 border-l border-white/10 pl-3">
              <DropdownChildren items={item.children} onNavigate={onNavigate} />
            </div>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function MenuDropdown({
  item,
  menuCategories,
  onNavigate,
}: {
  item: NavigationItem;
  menuCategories: MergedMenuCategory[];
  onNavigate: () => void;
}) {
  if (menuCategories.length === 0) {
    return <DropdownChildren items={item.children} onNavigate={onNavigate} />;
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3 border-b border-white/18 pb-2">
        <div className="min-w-0">
          <p className="text-[0.64rem] font-bold uppercase tracking-[0.3em] text-white">
            Menu categories
          </p>
        </div>
        <Link
          href={item.path}
          onClick={onNavigate}
          className="shrink-0 border border-white/18 bg-black/12 px-2.5 py-1.5 text-[0.68rem] font-semibold uppercase tracking-[0.18em] text-white/84 transition-colors hover:bg-white hover-storefront-text-primary"
        >
          Browse all menu
        </Link>
      </div>

      <div className="overflow-hidden border border-white/18 bg-white/10">
        <div className="grid gap-px bg-white/10 lg:grid-cols-4">
          {menuCategories.map((category) => {
            const imageAlt = category.image?.alt?.trim() || category.label;

            return (
              <Link
                key={category.key}
                href={category.path}
                onClick={onNavigate}
                className="group overflow-hidden bg-black/12 transition-colors duration-150 hover:bg-black/20 focus-visible:outline-none storefront-focus-ring"
              >
                <div className="relative aspect-square overflow-hidden border-b border-transparent bg-white p-2.5 transition-colors duration-150 group-hover:border-white/18">
                  <div className="pointer-events-none absolute inset-0 z-[1] border border-transparent transition-colors duration-150 group-hover:border-white/10" />
                  {category.image ? (
                    <Image
                      src={category.image.url}
                      alt={imageAlt}
                      fill
                      sizes="(min-width: 1280px) 9rem, (min-width: 1024px) 10rem, 100vw"
                      className="object-contain p-1.5"
                    />
                  ) : (
                      <div className="flex h-full items-center justify-center bg-white px-4 text-center text-[0.68rem] font-semibold uppercase tracking-[0.2em] text-white/55">
                        Kitchen
                      </div>
                  )}
                  <div className="pointer-events-none absolute inset-0 z-[2] bg-[linear-gradient(180deg,rgba(0,0,0,0)_0%,rgba(0,0,0,0)_62%,rgba(0,0,0,0.08)_100%)] opacity-0 transition-opacity duration-150 group-hover:opacity-100" />
                  <div className="pointer-events-none absolute inset-x-0 top-0 z-[2] h-2 bg-white opacity-0 transition-opacity duration-150 group-hover:opacity-100" />
                </div>
                <div className="border-t border-white/18 bg-black/12 px-2.5 py-2 text-center transition-colors duration-150 group-hover:bg-white">
                  <h4
                    className={`${menuTileLabelFont.className} text-[1.02rem] font-semibold leading-none tracking-[0.01em] text-white/84 transition-colors duration-150 group-hover:text-[#102313]`}
                  >
                    {category.label}
                  </h4>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function NavItem({ item, menuCategories = [] }: NavItemProps) {
  const router = useRouter();
  const containerRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<number | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const pathname = normalizePath(router?.asPath || router?.pathname || "");
  const isMenuItem = isMenuNavigationItem(item);
  const panelId = `${item.key}-panel`;

  const isActive = useMemo(
    () => hasActivePath(item, pathname),
    [item, pathname],
  );

  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current !== null) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const openMenu = useCallback(() => {
    clearCloseTimeout();
    setIsOpen(true);
  }, [clearCloseTimeout]);

  const closeMenu = useCallback(() => {
    clearCloseTimeout();
    setIsOpen(false);
  }, [clearCloseTimeout]);

  const closeMenuWithDelay = useCallback(() => {
    clearCloseTimeout();
    closeTimeoutRef.current = window.setTimeout(() => {
      setIsOpen(false);
      closeTimeoutRef.current = null;
    }, MENU_CLOSE_DELAY_MS);
  }, [clearCloseTimeout]);

  useEffect(() => {
    function handlePointerDown(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        closeMenu();
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        closeMenu();
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
      clearCloseTimeout();
    };
  }, [clearCloseTimeout, closeMenu]);

  useEffect(() => {
    if (!router?.events) {
      return undefined;
    }

    const handleRouteChange = () => {
      closeMenu();
    };

    router.events.on("routeChangeStart", handleRouteChange);

    return () => {
      router.events.off("routeChangeStart", handleRouteChange);
    };
  }, [closeMenu, router]);

  if (item.children.length === 0) {
    return (
      <Link
        href={item.path}
        className={`inline-flex items-center px-[1.28rem] py-[1.05rem] text-[0.85rem] font-semibold transition-colors lg:text-[0.85rem] lg:uppercase lg:tracking-[0.24em] ${
          isActive
            ? "bg-black/12 text-white"
            : "text-white/84 hover:bg-white/7 hover:text-white"
        }`}
      >
        <span className="inline-flex items-center gap-2.5">
          <StorefrontIcon
            asset={item.icon}
            context="navigation"
            label={item.label}
            className="h-4 w-4 text-current"
          />
          <span>{item.label}</span>
        </span>
      </Link>
    );
  }

  return (
    <div
      ref={containerRef}
      className="relative"
      onMouseEnter={openMenu}
      onMouseLeave={closeMenuWithDelay}
      onFocusCapture={openMenu}
      onBlurCapture={(event) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.relatedTarget as Node | null)
        ) {
          closeMenu();
        }
      }}
    >
      <div
        className={`flex items-center transition-colors ${
          isActive || isOpen
            ? "bg-black/12 text-white"
            : "bg-transparent text-white/84 hover:bg-white/7 hover:text-white"
        }`}
      >
        <Link
          href={item.path}
          onClick={closeMenu}
          className="px-[1.28rem] py-[1.05rem] text-[0.85rem] font-semibold lg:text-[0.85rem] lg:uppercase lg:tracking-[0.24em]"
        >
          <span className="inline-flex items-center gap-2.5">
            <StorefrontIcon
              asset={item.icon}
              context="navigation"
              label={item.label}
              className="h-4 w-4 text-current"
            />
            <span>{item.label}</span>
          </span>
        </Link>
        <button
          type="button"
          onMouseDown={(event) => {
            event.preventDefault();
          }}
          onClick={() => {
            if (isOpen) {
              closeMenu();
            } else {
              openMenu();
            }
          }}
          aria-expanded={isOpen}
          aria-controls={panelId}
          aria-haspopup="menu"
          aria-label={`Toggle ${item.label} submenu`}
          className={`px-3.5 py-[1.05rem] text-white/48 transition-[color,transform,background-color] duration-200 hover:bg-white/7 hover:text-white/72 ${
            isOpen ? "rotate-180" : ""
          }`}
        >
          <ChevronDownIcon />
        </button>
      </div>

      {isOpen ? (
        <>
          {isMenuItem ? (
            <div
              aria-hidden="true"
              className="absolute left-1/2 top-full z-[70] h-3 w-[min(40rem,calc(100vw-2rem))] -translate-x-1/2"
            />
          ) : null}
          <div
            id={panelId}
              className={`absolute top-full z-[80] rounded-none shadow-[0_20px_44px_-28px_rgba(0,0,0,0.28)] ring-1 ${
                isMenuItem
                  ? "left-1/2 mt-1 w-[min(40rem,calc(100vw-2rem))] -translate-x-1/2 storefront-bg-primary p-2.5 text-white ring-white/18"
                  : "storefront-surface-dark left-0 mt-1 min-w-[15rem] p-2.5 text-white ring-white/8"
              }`}
            >
            {isMenuItem ? (
              <MenuDropdown
                item={item}
                menuCategories={menuCategories}
                onNavigate={closeMenu}
              />
            ) : (
              <DropdownChildren items={item.children} onNavigate={closeMenu} />
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
